import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { trackCta } from '../../lib/analytics';

const competitors = [
  {
    name: 'HyTek Meet Manager',
    pitch: 'The desktop standard for meet directors running championship-grade meets with FAT timing.',
    bestFor: 'Hosting timed meets with FinishLynx or similar. Building heat sheets and printable programs.',
    gaps: 'Windows-only desktop install. No parent communications. No mobile. Single user. Built for meet day, not the season.',
    verdict: 'Keep it for the day you host. Use Baton for everything else.',
  },
  {
    name: 'Athletic.net',
    pitch: 'Public results database with light registration tooling.',
    bestFor: 'Posting results, looking up rankings, lightweight meet hosting.',
    gaps: 'No private team workspace. No real parent portal. Coach UI is dated.',
    verdict: 'Great for results. Pair with Baton for the team-and-parents side.',
  },
  {
    name: 'DirectAthletics MeetPro',
    pitch: 'Cross-platform meet management at a friendlier price than HyTek.',
    bestFor: 'Meet hosts on a budget who want timing-hardware integration without HyTek.',
    gaps: 'Same shape as HyTek: meet-day tool, single user, no parent comms.',
    verdict: 'Same trade-off as HyTek. Use Baton alongside it.',
  },
  {
    name: 'TFxCPro',
    pitch: '800+ feature coaching suite with FERPA compliance, physicals, audit logs, AI assistant.',
    bestFor: 'Larger US K-12 programs with compliance requirements and a head coach who wants every feature.',
    gaps: 'Heavy. Steep onboarding. Overkill for clubs and small programs.',
    verdict: "If you need FERPA-grade compliance and don't mind the complexity, evaluate it. Otherwise Baton is faster to start.",
  },
  {
    name: 'TeamSnap / SportsEngine',
    pitch: 'Sport-agnostic team management — calendars, chat, payments.',
    bestFor: 'General team admin: schedule, RSVPs, dues collection.',
    gaps: "Doesn't understand T&F: events, divisions, heats, lanes, seed marks.",
    verdict: 'Fine for chat and calendars. Bad fit for actual T&F entries.',
  },
];

export default function ComparePage() {
  useEffect(() => {
    document.title = 'Baton vs HyTek vs Athletic.net vs MeetPro vs TFxCPro — comparison';
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 6, md: 9 } }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
        Baton vs the alternatives
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 640 }}>
        An honest second opinion. We'd rather you pick the right tool — even if it isn't us — than buy
        the wrong one. Here's the lay of the land in 2026.
      </Typography>

      <Stack spacing={2.5} sx={{ mt: 5 }}>
        {competitors.map((c) => (
          <Card key={c.name} variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{c.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {c.pitch}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {[
                  { label: 'Best for', body: c.bestFor, color: '#0f5132' },
                  { label: 'Gaps', body: c.gaps, color: '#b42318' },
                  { label: 'Our take', body: c.verdict, color: '#0b5394' },
                ].map((b) => (
                  <Grid item xs={12} sm={4} key={b.label}>
                    <Typography variant="overline" sx={{ color: b.color, fontWeight: 700, letterSpacing: 1 }}>
                      {b.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {b.body}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Box
        sx={{
          mt: 5,
          p: 4,
          borderRadius: 3,
          bgcolor: '#eff6fc',
          border: '1px solid #a8c8e3',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Where Baton fits
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Baton is the team-and-parent-comms layer. We don't replace HyTek when you're hosting a championship.
          We don't compete with Athletic.net for public rankings. We sit in the gap none of them fills:
          the day-to-day workspace where coaches manage rosters, register athletes into events, and
          send parents one polished email per meet.
        </Typography>
        <Button
          component={RouterLink}
          to="/#waitlist"
          variant="contained"
          sx={{ mt: 2 }}
          aria-label="Get early access"
          onClick={() => trackCta('Waitlist', 'compare')}
        >
          Get early access →
        </Button>
      </Box>
    </Container>
  );
}
